import {
    patternsRoutes,
    reportsRoutes
} from '../routes'
import { getNumerosReportes, findAsignationRevisors } from '../routes/reports.routes'
import {
    executionReportsDatabase,
    reportsDatabase
} from '../indexedDB'
import axios from 'axios'
import apiClient from '../connections/api'

const ReportsService = {
    /**
     * Fetches reports based on online status, user roles, and selected site.
     * Handles both online API calls and offline IndexedDB data.
     * @param {object} params
     * @param {boolean} params.isOnline
     * @param {object} params.userData
     * @param {boolean} params.isAuthenticated
     * @param {boolean} params.admin
     * @param {boolean} params.isOperator
     * @param {boolean} params.isSapExecutive
     * @param {boolean} params.isOperatorInspector
     * @param {boolean} params.isShiftManager
     * @param {boolean} params.isChiefMachinery
     * @param {string} params.siteSelected
     * @param {Function} params.setLoadingLogo
     * @param {Function} params.setNumerosEnReportes
     * @param {Function} params.setAssignmentsError
     * @param {Function} params.setStatusReports
     * @param {Function} params.setWaitReports
     * @param {Function} params.setReportsToShow
     * @param {Function} params.downloadPautas // This should probably be in patternsService or similar
     * @returns {Promise<Array>} A promise that resolves to the reports data.
     */
    getReportsFromApi: async ({
        userData,
        isAuthenticated,
        admin,
        isOperator,
        isSapExecutive,
        isOperatorInspector,
        isUniversalInspector,
        isUniversalChief,
        isShiftManager,
        isChiefMachinery,
        siteSelected,
        setLoadingLogo,
        setAssignmentsError,
        setStatusReports,
        setWaitReports,
        setReportsToShow,
        downloadPautas
    }) => {
        const isAdminOrManager = admin || isSapExecutive || isShiftManager || isChiefMachinery || isUniversalInspector || isUniversalChief
        const isPureOperator = isOperator && !isAdminOrManager

        if (isAdminOrManager && !siteSelected) {
            console.warn('ReportsService: getReports call for Admin/Manager skipped because siteSelected is undefined.')
            setStatusReports(false)
            setLoadingLogo(false)
            return []
        }

        setAssignmentsError(null)
        setStatusReports(true)
        setWaitReports(true)

        let reports = []

        if (isPureOperator) {
            try {
                const response = await reportsRoutes.findMyAssignations()
                const reportsFromApi = response.data.reports || []
                const ejecuciones = await executionReportsDatabase.consultarTodas()

                reports = reportsFromApi.map((report) => {
                    const ejecucion = ejecuciones.find(
                        (e) => (String(e.reportId) === String(report._id) || String(e.report?._id) === String(report._id) || String(e.report) === String(report._id))
                    )

                    if (ejecucion) {
                        report.tieneEjecucionEnDispositivo = ejecucion
                        if (ejecucion.inicio && ejecucion.termino === null) {
                            if (!report.ejecuciones) report.ejecuciones = []
                            const hasOngoing = report.ejecuciones.some(e => e.termino === null && String(e.userId?._id || e.userId) === String(ejecucion.userId?._id || ejecucion.userId))
                            if (!hasOngoing) {
                                report.ejecuciones.push(ejecucion)
                            }
                            if (ejecucion.progress !== undefined && ejecucion.progress > (report.progress || 0)) {
                                report.progress = ejecucion.progress
                            }
                        }
                    } else {
                        report.tieneEjecucionEnDispositivo = null
                    }
                    return report
                })
            } catch (error) {
                if (!navigator.onLine || !error.response) {
                    console.warn('Network offline or connection lost during fetch. Falling back to local data without error state.')
                } else if (error.response && error.response.status === 403) {
                    console.warn('Permission error fetching assignments for operator:', error)
                    setAssignmentsError('No tienes permiso para ver tus asignaciones. Por favor, contacta a un administrador.')
                } else if (error.response && error.response.status === 404) {
                    console.warn('No assignments found for operator (404). Treating as empty list.')
                    setAssignmentsError('No tienes reportes asignados en este momento.')
                    reports = []
                } else {
                    console.error('Failed to fetch user assignations:', error)
                    setAssignmentsError('Error al cargar asignaciones. Intente nuevamente.')
                }
                if (!reports) reports = []
            } finally {
                setLoadingLogo(false)
                setStatusReports(false)
                setWaitReports(false)
            }
        } else if (isAdminOrManager) {
            const siteId = (siteSelected?.idObra === 'none' || siteSelected?._id === 'all') ? 'all' : (siteSelected?._id || siteSelected?.idObra)
            console.log(`Fetching reports for Admin/Manager for site: ${siteId}`)
            try {
                if (isOperator || isUniversalInspector) {
                    const responseReports = await reportsRoutes.findMyAssignations()
                    const ejecuciones = await executionReportsDatabase.consultarTodas()
                    const reportsFinal = await Promise.all(responseReports.data.reports.map(async (report) => {
                        const ejecucion = ejecuciones.find(
                            (e) => (String(e.reportId) === String(report._id) || String(e.report?._id) === String(report._id) || String(e.report) === String(report._id))
                        )
                        if (ejecucion) {
                            report.tieneEjecucionEnDispositivo = ejecucion
                            if (ejecucion.inicio && ejecucion.termino === null) {
                                if (!report.ejecuciones) report.ejecuciones = []
                                const hasOngoing = report.ejecuciones.some(e => e.termino === null && String(e.userId?._id || e.userId) === String(ejecucion.userId?._id || ejecucion.userId))
                                if (!hasOngoing) {
                                    report.ejecuciones.push(ejecucion)
                                }
                                if (ejecucion.progress !== undefined && ejecucion.progress > (report.progress || 0)) {
                                    report.progress = ejecucion.progress
                                }
                            }
                        } else {
                            report.tieneEjecucionEnDispositivo = null
                        }
                        if (report.origen && report.usersAssigned && report.usersAssigned.length > 0) {
                            report.origen = false
                        }
                        return report
                    }))
                    reports = reportsFinal
                } else { // A pure manager/admin
                    reports = []
                }
            } catch (error) {
                console.error('An error occurred while fetching manager/admin reports:', error)
                reports = []
            } finally {
                if (isAdminOrManager && !isUniversalInspector) {
                    downloadPautas()
                }
                setStatusReports(false)
                setLoadingLogo(false)
                setWaitReports(false)
            }
        }
        return reports
    },

    saveReportsFromLocalDatabase: async () => {
        return new Promise((resolve) => {
            const process = async () => {
                const reportsCache = await reportsDatabase.consultar()
                reportsCache.forEach((report, i) => {
                    if (i === reportsCache.length - 1) {
                        resolve({
                            state: true
                        })
                    }
                })
            }
            process()
        })
    },

    /**
     * Processes report data based on user actions (send, reject, close).
     * @param {object} params
     * @param {object} params.report - The current report object.
     * @param {number} params.progress - The completion progress of the report.
     * @param {string} params.messageType - Type of action ('ok', 'rejectReport').
     * @param {object} params.data - Data from the dialog (comentario, operarios).
     * @param {object} params.userData - Current user data.
     * @returns {object} The updated report data.
     */
    processReportData: ({ report, progress, messageType, data, userData }) => {
        const navigateType = messageType === 'rejectReport' ? 'reject-to-previous-level' : 'sending-to-next-level'

        const historyItem = {
            operadores: (report.level < 1) ? data.operarios : null,
            id: Date.now(),
            userSendingData: userData._id,
            type: navigateType,
            message: data.comentario
        }
        const reportData = {
            ...report,
            updatedBy: userData._id
        }
        if (navigateType === 'sending-to-next-level') {
            if (reportData.level === 0) {
                reportData.state = 'En proceso'
                reportData.endReport = Date.now()

                if (reportData.reportType === 'Mantención') {
                    reportData.level = reportData.level + 1
                } else {
                    reportData.level = reportData.level + 2
                }
            } else if (reportData.level === 1) {
                reportData.shiftManagerApprovedBy = userData._id
                reportData.shiftManagerApprovedDate = Date.now()
                reportData.level = reportData.level + 1
            } else if (reportData.level === 2) {
                reportData.chiefMachineryApprovedBy = userData._id
                reportData.chiefMachineryApprovedDate = Date.now()
                reportData.level = reportData.level + 1
                reportData.state = 'Por cerrar'
            } else if (reportData.level === 3) {
                reportData.sapExecutiveApprovedBy = userData._id
                reportData.dateClose = Date.now()
                reportData.state = 'Completadas'
                reportData.level = reportData.level + 1
            }
        } else if (navigateType === 'reject-to-previous-level') {
            reportData.state = 'Reasignar'
            if (reportData.level === 1) {
                reportData.endReport = null
            }
            if (reportData.reportType === 'Mantención') {
                reportData.level = reportData.level - 1
            } else {
                if (reportData.level === 2) {
                    reportData.level = reportData.level - 2
                } else {
                    reportData.level = reportData.level - 1
                }
            }
        } else if (messageType === 'reject-for-end-day') {
            reportData.state = 'Asignar'
            reportData.usersAssigned = []
            reportData.level = 0
        }
        reportData.history.push(historyItem)

        let emailing = 'termino-jornada'
        if (navigateType === 'sending-to-next-level') {
            emailing = `termino-orden-${reportData.level}`
        } else if (navigateType === 'reject-to-previous-level') {
            emailing = `rechazo-orden-${reportData.level}`
        } else if (messageType === 'reject-for-end-day') {
            emailing = 'termino-jornada'
        }

        const fullNameWorker = `${userData.name} ${userData.lastName}`
        const emailsToSend = []
        reportData.progress = progress
        reportData.emailing = emailing
        reportData.fullNameWorker = fullNameWorker
        // Ensure the custom message is passed to the backend
        reportData.message = data.comentario
        if (report.level === 0) {
            reportData.operadores = data.operarios
        }
        reportData.emailsToSend = emailsToSend
        reportData.hiddeLocal = true
        return reportData
    },

    saveReport: async (reportData) => {
        try {
            await reportsRoutes.editReport(reportData)
        } catch (error) {
            console.error('Error saving report:', error)
        }
    },

    editReport: async (reportData) => {
        try {
            const response = await reportsRoutes.editReport(reportData)
            return response
        } catch (error) {
            console.error('Error editing report:', error)
            throw error
        }
    },

    saveReportLocal: async (reportData) => {
        try {
            await reportsDatabase.actualizar(reportData)
            const response = await ReportsService.saveReport(reportData) // Call service's saveReport
            // setLoading(false) // context action
            return response
        } catch (error) {
            console.error('Error saving report locally:', error)
            // setLoading(false) // context action
            return error
        }
    },

    createReport: async (reportData) => {
        try {
            const response = await reportsRoutes.newReport(reportData)
            return response
        } catch (error) {
            console.error('Error creating report:', error)
        }
    },

    borrarListaReportes: async (ids) => {
        const response = await apiClient.post('/reports/borrarListaReportes', { ids })
        return response
    },

    reportesPaginados: async (estado, desde, maximo, obra, tipoReporte, sortField, sortOrder, deleted = false) => {
        const respuesta = await reportsRoutes.reportesPaginados(estado, desde, maximo, obra, tipoReporte, sortField, sortOrder, deleted)

        let reportes = respuesta.reportes || []
        try {
            const ejecuciones = await executionReportsDatabase.consultarTodas()
            if (ejecuciones && ejecuciones.length > 0) {
                reportes = reportes.map(report => {
                    const ejecucion = ejecuciones.find(
                        (e) => (String(e.reportId) === String(report._id) || String(e.report?._id) === String(report._id) || String(e.report) === String(report._id))
                    )
                    if (ejecucion) {
                        report.tieneEjecucionEnDispositivo = ejecucion
                        if (ejecucion.inicio && ejecucion.termino === null) {
                            if (!report.ejecuciones) report.ejecuciones = []
                            const hasOngoing = report.ejecuciones.some(e => e.termino === null && String(e.userId?._id || e.userId) === String(ejecucion.userId?._id || ejecucion.userId))
                            if (!hasOngoing) {
                                report.ejecuciones.push(ejecucion)
                            }
                            if (ejecucion.progress !== undefined && ejecucion.progress > (report.progress || 0)) {
                                report.progress = ejecucion.progress
                            }
                        }
                    } else {
                        report.tieneEjecucionEnDispositivo = null
                    }
                    return report
                })
            }
        } catch (e) {
            console.error('Error merging local executions in reportesPaginados:', e)
        }

        return {
            reportes: reportes,
            totalItems: respuesta.totalItems || 0
        }
    },

    restoreReport: async (id) => {
        const response = await reportsRoutes.restoreReport(id)
        return response
    },

    reportesPaginadosFiltrados: async (estado, desde, maximo, obra, tipoReporte, global, value, tipoDeBusqueda, sortField, sortOrder) => {
        const respuesta = await reportsRoutes.reportesPaginadosFiltrados(estado, desde, maximo, obra, tipoReporte, global, value, tipoDeBusqueda, sortField, sortOrder)
        let reportes = respuesta.reportes || []
        try {
            const ejecuciones = await executionReportsDatabase.consultarTodas()
            if (ejecuciones && ejecuciones.length > 0) {
                reportes = reportes.map(report => {
                    const ejecucion = ejecuciones.find(
                        (e) => (String(e.reportId) === String(report._id) || String(e.report?._id) === String(report._id) || String(e.report) === String(report._id))
                    )
                    if (ejecucion) {
                        report.tieneEjecucionEnDispositivo = ejecucion
                        if (ejecucion.inicio && ejecucion.termino === null) {
                            if (!report.ejecuciones) report.ejecuciones = []
                            const hasOngoing = report.ejecuciones.some(e => e.termino === null && String(e.userId?._id || e.userId) === String(ejecucion.userId?._id || ejecucion.userId))
                            if (!hasOngoing) {
                                report.ejecuciones.push(ejecucion)
                            }
                            if (ejecucion.progress !== undefined && ejecucion.progress > (report.progress || 0)) {
                                report.progress = ejecucion.progress
                            }
                        }
                    } else {
                        report.tieneEjecucionEnDispositivo = null
                    }
                    return report
                })
            }
        } catch (e) {
            console.error('Error merging local executions in reportesPaginadosFiltrados:', e)
        }
        return {
            reportes: reportes,
            numeroItems: respuesta.contador
        }
    },

    getNumbers: async (siteId) => {
        // siteId ya viene procesado como ObjectId o 'all' desde el contexto/componente
        const reportNumbersResponse = await getNumerosReportes(siteId)
        return reportNumbersResponse
    },

    // --- Offline Specific ---
    getReportsFromIndexedDb: async () => {
        const reportsResponse = await reportsDatabase.consultar()
        console.log(`[ReportsService] Se cargaron ${reportsResponse.length} reportes desde IndexedDB.`)

        const validReports = reportsResponse.filter(report => !report.hiddeLocal)

        try {
            const ejecuciones = await executionReportsDatabase.consultarTodas()
            return validReports.map(report => {
                const ejecucion = ejecuciones.find((e) => {
                    const eId = e.reportId || e.report?._id || e.report
                    return eId && report._id && String(eId) === String(report._id)
                })
                if (ejecucion) {
                    report.tieneEjecucionEnDispositivo = ejecucion
                }
                return report
            })
        } catch (error) {
            console.error('[ReportsService] Error mapping executions to offline reports:', error)
            return validReports
        }
    },

    saveReportsToIndexedDb: async (reportsToSave) => {
        if (!Array.isArray(reportsToSave)) {
            console.warn('[ReportsService] saveReportsToIndexedDb fue llamado con data no válida.')
            return
        }
        console.log(`[ReportsService] Iniciando guardado de ${reportsToSave.length} reportes en IndexedDB.`)
        for (const report of reportsToSave) {
            await reportsDatabase.actualizar(report)
        }
        const elementsToremoved = await reportsDatabase.consultar()
        for (const el of elementsToremoved) {
            const filtered = reportsToSave.filter(report => {
                if (el.newIdIndex) {
                    if (el.newIdIndex === report.newIdIndex) {
                        return report
                    }
                } else {
                    if (el.idIndex === report.idIndex) {
                        return report
                    }
                }
                return null // Add explicit return for filter
            })
            if (filtered.length === 0) {
                await reportsDatabase.eliminar(el.newIdIndex ? el.newIdIndex : el.idIndex)
            }
        }
        console.log(`[ReportsService] Sincronización de ${reportsToSave.length} reportes a IndexedDB completada.`)
    },

    filterReports: async (reportsData) => { // Renamed param to avoid clash with ReportsService.reports
        const reportsDB = await reportsDatabase.consultar()
        const reportsToDelete = []
        reportsDB.forEach((reporte) => {
            const reporteFiltrado = reportsData.filter(r => {
                if (r._id === reporte._id) {
                    return r
                }
                return null // Add explicit return for filter
            })
            if (reporteFiltrado.length === 0) {
                reportsToDelete.push(reporte)
            }
        })
        for (const r of reportsToDelete) {
            await reportsDatabase.eliminar(r.newIdIndex ? r.newIdIndex : r.idIndex)
        }
    },

    // --- Other Utility Functions ---
    findDuplicates: (array) => {
        const duplicates = []

        for (let i = 0; i < array.length; i++) {
            for (let j = i + 1; j < array.length; j++) {
                if (array[i]._id === array[j]._id && !duplicates.includes(array[i]._id)) {
                    duplicates.push(array[i])
                }
            }
        }
        return duplicates
    },

    getReportByIndex: async (otIndex) => {
        return await reportsRoutes.getReportByIndex(otIndex)
    },

    // --- Patterns (Currently in context for getReports) ---
    // This should ideally be its own service (PatternsService)
    downloadPautas: async () => {
        const respuestaPautas = await patternsRoutes.getPatternsFields()
        return respuestaPautas.pautas
    },

    assignReport: async (reportId, assignmentData) => {
        try {
            const response = await reportsRoutes.assignReport(reportId, assignmentData)
            return response
        } catch (error) {
            console.error('Error assigning report:', error)
            throw error
        }
    },

    getKPIs: async ({ startDate, endDate, site, reportType }) => {
        try {
            const params = {}

            if (startDate) {
                params.startDate = new Date(startDate).toISOString()
            }
            if (endDate) {
                const end = new Date(endDate)
                end.setHours(23, 59, 59, 999)
                params.endDate = end.toISOString()
            }

            params.site = (site && site !== 'none') ? site : 'all'
            params.reportType = reportType || 'todos'

            const response = await reportsRoutes.getKPIs(params)
            return response.data
        } catch (error) {
            console.error('Error fetching KPIs:', error)
            throw error
        }
    },

    checkOpenReportForUser: async () => {
        try {
            const response = await reportsRoutes.checkOpenReportForUser()
            return response
        } catch (error) {
            console.error('Error checking for open report:', error)
            return { openReports: [] }
        }
    }
}

export default ReportsService
